import React, { useLayoutEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Row,
  Col,
  Table,
  Button,
  Tag,
  Spin,
  Space,
  Input,
  Modal,
  InputNumber,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import { getAPI, postAPI, displayMessage } from "../../utils/common";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import {
  KITCHEN_PR_DETAIL,
  KITCHEN_PR_UPDATE_STATUS,
} from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";

function KitchenPRView() {
  const { Content } = Layout;
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pr, setPR] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState("");
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // Additional state
  const [approvedModalVisible, setApprovedModalVisible] = useState(false);
  const [approvedItems, setApprovedItems] = useState([]);
  const [approvedPRRemark, setApprovedPRRemark] = useState("");

  const fetchPR = () => {
    setLoading(true);
    postAPI(
      KITCHEN_PR_DETAIL,
      { pr_id: id },
      (res) => {
        if (res.status === true) {
          const prData = res.data;
          prData.pr_logs =
            typeof prData.pr_logs === "string"
              ? JSON.parse(prData.pr_logs)
              : prData.pr_logs || [];
          setPR(prData);
        } else {
          setPR(null);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        setPR(null);
      },
    );
  };

  useLayoutEffect(() => {
    fetchPR();
  }, [id]);

  const updateStatus = (status) => {
    if (status === "Approved") {
      // Prepare modal data
      setApprovedPRRemark(pr.remark || "");
      const itemsForApproval = (pr.items || []).map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        approved_qty: item.item_requried_stock,
        item_remark: item.item_remark || "",
      }));
      setApprovedItems(itemsForApproval);
      setApprovedModalVisible(true);
    } else {
      // Existing modal for Reject/Purchased
      setNewStatus(status);
      setNote("");
      setStatusModalVisible(true);
    }
  };

  // Handle Approved modal change
  const handleApprovedItemChange = (index, field, value) => {
    const updated = [...approvedItems];
    updated[index][field] = value;
    setApprovedItems(updated);
  };

  // Submit Approved data
  const handleApprovedSubmit = () => {
    setUpdating(true);
    postAPI(
      KITCHEN_PR_UPDATE_STATUS,
      {
        pr_id: id,
        status: "Approved",
        by: LS_SERVICE.get("name"),
        note: approvedPRRemark, // main remark
        items: approvedItems, // approved qty + item remarks
      },
      (res) => {
        if (res.status === true) {
          displayMessage(SUCCESS_MESSAGE, res.message);
          fetchPR();
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setUpdating(false);
        setApprovedModalVisible(false);
      },
      () => {
        displayMessage(ERROR_MSG_TYPE, "Unable to approve PR");
        setUpdating(false);
      },
    );
  };

  const handleStatusUpdate = () => {
    if (!newStatus) return;

    setUpdating(true);
    postAPI(
      KITCHEN_PR_UPDATE_STATUS,
      { pr_id: id, status: newStatus, by: LS_SERVICE.get("name"), note },
      (res) => {
        if (res.status === true) {
          displayMessage(SUCCESS_MESSAGE, res.message);
          fetchPR();
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setUpdating(false);
        setStatusModalVisible(false);
      },
      () => {
        setUpdating(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to update status");
      },
    );
  };

  const itemColumns = [
    { title: "Item Name", dataIndex: "item_name", key: "item_name" },
    { title: "Requested Qty", dataIndex: "item_requried_stock", key: "req" },
    {
      title: "Approved Qty",
      dataIndex: "item_approved_stock",
      key: "approved",
    },
    { title: "Remark", dataIndex: "item_remark", key: "remark" },
  ];

  const logColumns = [
    { title: "Action", dataIndex: "action", key: "action" },
    { title: "By", dataIndex: "by", key: "by" },
    { title: "Note", dataIndex: "note", key: "note" },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text).format("DD MMM YYYY HH:mm"),
    },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Spin spinning={loading || !pr}>
          <Card
            loading={loading || !pr}
            title={`Purchase Request #${pr?.id}`}
            extra={
              <Button type="primary" ghost onClick={() => navigate(-1)}>
                <RollbackOutlined /> Back
              </Button>
            }
          >
            {pr && (
              <>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <b>Requested By:</b> {pr?.requested_by}
                  </Col>
                  <Col span={8}>
                    <b>Priority:</b>{" "}
                    {pr.priority === "Urgent" ? (
                      <Tag color="red">{pr.priority}</Tag>
                    ) : (
                      <Tag color="blue">{pr.priority}</Tag>
                    )}
                  </Col>
                  <Col span={8}>
                    <b>Status:</b>{" "}
                    <Tag
                      color={
                        pr.status === "Pending"
                          ? "orange"
                          : pr.status === "Approved"
                            ? "green"
                            : pr.status === "Reject"
                              ? "red"
                              : "purple"
                      }
                    >
                      {pr.status}
                    </Tag>
                  </Col>
                </Row>

                <Row style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <b>Remark:</b> {pr?.remark || "-"}
                  </Col>
                </Row>

                <Card
                  title="Requested Items"
                  size="small"
                  style={{ marginTop: 16 }}
                >
                  <Table
                    columns={itemColumns}
                    dataSource={pr.items || []}
                    pagination={false}
                    rowKey="item_id"
                  />
                </Card>

                <Card
                  title="Action Logs"
                  size="small"
                  style={{ marginTop: 16 }}
                >
                  <Table
                    columns={logColumns}
                    dataSource={pr.pr_logs || []}
                    pagination={false}
                    rowKey={(row, idx) => idx}
                  />
                </Card>

                <Space style={{ marginTop: 16 }}>
                  {["Approved", "Reject", "Purchased"].map((status) => {
                    if (!pr) return null;

                    const userType = LS_SERVICE.get("user_type");

                    // Role-based access: Approved / Reject only for superAdmin or admin
                    if (
                      (status === "Approved" || status === "Reject") &&
                      !["superAdmin", "admin"].includes(userType)
                    ) {
                      return null;
                    }

                    // Workflow rules
                    if (pr.status === "Approved") {
                      if (status === "Approved" || status === "Reject")
                        return null;
                    } else if (pr.status === "Reject") {
                      if (status === "Approved" || status === "Purchased")
                        return null;
                    } else if (pr.status === "Purchased") {
                      return null;
                    }

                    // Purchased only allowed after approval
                    if (status === "Purchased" && pr.status !== "Approved")
                      return null;

                    return (
                      <Button
                        key={status}
                        type="primary"
                        onClick={() => updateStatus(status)}
                      >
                        {status}
                      </Button>
                    );
                  })}
                </Space>
              </>
            )}
          </Card>
        </Spin>

        {/* Modal for status update */}
        <Modal
          title={`Update Status to ${newStatus}`}
          open={statusModalVisible}
          onOk={handleStatusUpdate}
          onCancel={() => setStatusModalVisible(false)}
          confirmLoading={updating}
        >
          <Input.TextArea
            rows={3}
            placeholder="Optional note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Modal>

        {/* Approved Items Modal */}
        <Modal
          title="Approve Purchase Request"
          open={approvedModalVisible}
          onOk={handleApprovedSubmit}
          onCancel={() => setApprovedModalVisible(false)}
          width={800}
          confirmLoading={updating}
        >
          <div style={{ marginBottom: 12 }}>
            <b>Main Remark:</b>
            <Input.TextArea
              rows={2}
              value={approvedPRRemark}
              onChange={(e) => setApprovedPRRemark(e.target.value)}
              placeholder="Update main PR remark if needed"
            />
          </div>

          <Table
            columns={[
              {
                title: "Item Name",
                dataIndex: "item_name",
                key: "item_name",
                width: 50,
              },
              {
                title: "Approved Qty",
                dataIndex: "approved_qty",
                key: "approved_qty",
                width: 20,
                render: (text, record, index) => (
                  <InputNumber
                    min={0}
                    value={approvedItems[index]?.approved_qty}
                    onChange={(val) =>
                      handleApprovedItemChange(index, "approved_qty", val)
                    }
                    style={{ width: "100%" }}
                  />
                ),
              },
              {
                title: "Item Remark",
                dataIndex: "item_remark",
                key: "item_remark",
                width: 100,
                render: (text, record, index) => (
                  <Input
                    value={approvedItems[index]?.item_remark}
                    onChange={(e) =>
                      handleApprovedItemChange(
                        index,
                        "item_remark",
                        e.target.value,
                      )
                    }
                  />
                ),
              },
            ]}
            dataSource={approvedItems}
            rowKey="item_id"
            pagination={false}
          />
        </Modal>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenPRView;
